import React, { useState } from "react";
import styled from "styled-components";

import Button from "../../../components/button/Button";
import OfferReviewModal from "./OfferReviewModal";
import { reviewRatingIcon, reviewRatingLabel } from "./reviewRating";
import type {
  DmReviewEntry,
  DmReviewsBlock,
} from "../../../app/services/dmThreads";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`;

const SectionTitle = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-gray);
  font-weight: 600;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--soremo-border);
  background-color: #fff;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .material-symbols-rounded {
    font-size: 20px;
    color: var(--soremo-blue);
  }
`;

const RatingLabel = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const CardNote = styled.p.attrs({ className: "bodytext-13" })`
  margin: 0;
  white-space: pre-wrap;
  color: var(--soremo-gray);
`;

const EmptyHint = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
`;

interface ReviewCardProps {
  title: string;
  review: DmReviewEntry;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ title, review }) => {
  const icon = reviewRatingIcon(review.rating);
  const label = reviewRatingLabel(review.rating);
  return (
    <Card>
      <CardHeader>
        {icon && <span className="material-symbols-rounded">{icon}</span>}
        <span className="bodytext-13">{title}</span>
      </CardHeader>
      {label && <RatingLabel>{label}</RatingLabel>}
      {review.note && <CardNote>{review.note}</CardNote>}
    </Card>
  );
};

export interface OfferReviewSectionProps {
  projectId: string;
  dmId: string;
  /** Detail API の reviews ブロック（subcontract かつ completed のときのみ存在）。 */
  reviews: DmReviewsBlock | undefined;
  /** 閲覧者の立場。当事者でない（参加者外）の場合は null。 */
  viewerRole: "requester" | "contractor" | null;
  requesterName: string | null;
  contractorName: string | null;
}

/**
 * オファー完了後のレビューセクション（タイムライン末尾に表示）。
 *
 * - 当事者（委託者 / 受託者）には、自分が未投稿なら「レビューを書く」CTA、
 *   投稿済みなら自分のレビューカード + 編集導線を出す。
 * - 相手のレビューがあればカードで表示する（DM 当事者間では本文を共有する）。
 */
const OfferReviewSection: React.FC<OfferReviewSectionProps> = ({
  projectId,
  dmId,
  reviews,
  viewerRole,
  requesterName,
  contractorName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const myReview =
    viewerRole === "requester"
      ? reviews?.by_requester ?? null
      : viewerRole === "contractor"
        ? reviews?.by_contractor ?? null
        : null;
  const theirReview =
    viewerRole === "requester"
      ? reviews?.by_contractor ?? null
      : viewerRole === "contractor"
        ? reviews?.by_requester ?? null
        : null;

  const revieweeName =
    (viewerRole === "requester" ? contractorName : requesterName) ?? "相手";

  const isParticipant = viewerRole !== null;

  // 当事者でなく、双方のレビューも無ければ何も表示しない。
  if (!isParticipant && !myReview && !theirReview) {
    return null;
  }

  return (
    <Wrapper>
      <SectionTitle>レビュー</SectionTitle>

      {myReview ? (
        <>
          <ReviewCard title="あなたのレビュー" review={myReview} />
          {isParticipant && (
            <div className="u-row u-gap8">
              <Button
                size="small"
                variant="text"
                theme="secondary"
                leftIcon="edit"
                onClick={() => setIsModalOpen(true)}
              >
                レビューを編集
              </Button>
            </div>
          )}
        </>
      ) : isParticipant ? (
        <div className="u-col u-gap8 u-w100">
          <EmptyHint>
            検収が完了しました。{revieweeName}さんへのレビューを書きましょう。
          </EmptyHint>
          <div className="u-row">
            <Button
              size="medium"
              variant="filled"
              theme="primary"
              rightIcon="rate_review"
              onClick={() => setIsModalOpen(true)}
            >
              レビューを書く
            </Button>
          </div>
        </div>
      ) : null}

      {theirReview && (
        <ReviewCard
          title={`${revieweeName}さんからのレビュー`}
          review={theirReview}
        />
      )}

      {isModalOpen && isParticipant && (
        <OfferReviewModal
          projectId={projectId}
          dmId={dmId}
          revieweeName={revieweeName}
          existing={myReview}
          onClose={() => setIsModalOpen(false)}
          onSubmitted={() => setIsModalOpen(false)}
        />
      )}
    </Wrapper>
  );
};

export default OfferReviewSection;
